<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { required } from "vuelidate/lib/validators";
import SupportTicket from "@/services/westore/entities/SupportTicket";
import Faqs from "../../../components/faqs.vue";
import FormSkeleton from "../../../components/skeletons/form-skeleton.vue";

export default {
  page: {
    title: "Create support ticket",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    Faqs,
    FormSkeleton,
  },
  data() {
    return {
      title: "Submit a Ticket",
      subtitle: "Please fill all the mandatory fields.",
      submitted: false,
      showContentLoader: true,
      isSuccess: false,
      loader: false,
      successMessage: "",
      formInputs: {
        urgency: null,
        subject: "",
        description: "",
      },
      priorityLevels: [
        {
          id: 1,
          name: "High",
        },
        {
          id: 2,
          name: "Normal",
        },
        {
          id: 3,
          name: "Low",
        },
      ],
      faqs: [
        { question: "Question 1", answer: "Answer Answer" },
        {
          question: "How long would it take for the order to be executed?",
          answer:
            "To enable automatic updates for Admin Columns Pro, enter your license key. If you don't have a license key, please see details & pricing.",
        },
      ],
      expandForm: false,
    };
  },
  validations: {
    formInputs: {
      urgency: {
        required,
      },
      subject: {
        required,
      },
      description: {
        required,
      },
    },
  },
  created() {
    this.init();
  },
  computed: {
    checkLoader() {
      return this.loader;
    },
  },
  methods: {
    init() {
      this.formInputs.urgency = this.priorityLevels[0].id;
      setTimeout(() => {
        this.showContentLoader = false;
      }, 1000);
    },
    submitForm() {
      this.submitted = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loader = true;
        SupportTicket.store(this.formInputs).then((res) => {
          this.successMessage = `Support Ticket # ${res.data.data.ticket_number} has been created successfully`;
          this.isSuccess = true;
          setTimeout(
            () =>
              this.$router.push({
                name: "support-ticket-details",
                params: { id: res.data.data.id },
              }),
            3000
          );
        });
      }
    },
    toggleFormListener(val) {
      this.expandForm = val;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :subtitle="subtitle" />
    <div class="row">
      <div
        class="col-md-9 col-sm-12 order-2 order-sm-12"
        :class="expandForm && 'col-md-11 col-sm-12'"
      >
        <div class="card">
          <FormSkeleton v-if="showContentLoader" />
          <div class="card-body" v-else>
            <div class="row">
              <div class="col-12">
                <form class="form-horizontal" @submit.prevent="submitForm">
                  <!-- Urgency-->
                  <b-form-group class="mb-3" label-size="lg" :label="$t('Urgency *')">
                    <b-form-select
                      v-model="formInputs.urgency"
                      class="form-select"
                      value-field="id"
                      text-field="name"
                      :options="priorityLevels"
                      type="text"
                      :class="{
                        'is-invalid': submitted && $v.formInputs.urgency.$error,
                      }"
                    ></b-form-select>
                  </b-form-group>
                  <!-- Subject -->
                  <b-form-group class="mb-3" label-size="lg" :label="$t('Subject *')">
                    <b-form-input
                      v-model="formInputs.subject"
                      type="text"
                      :class="{
                        'is-invalid': submitted && $v.formInputs.subject.$error,
                      }"
                    ></b-form-input>
                  </b-form-group>
                  <!-- Description -->
                  <b-form-group
                    class="mb-3"
                    label-size="lg"
                    :label="$t('Description *')"
                  >
                    <b-form-textarea
                      v-model="formInputs.description"
                      rows="8"
                      :class="{
                        'is-invalid':
                          submitted && $v.formInputs.description.$error,
                      }"
                    ></b-form-textarea>
                  </b-form-group>
                  <!-- Submit -->
                  <div class="row float-end">
                    <div class="col-md-2">
                      <v-progress-circular
                        v-if="checkLoader"
                        :size="30"
                        color="secondary"
                        indeterminate
                      ></v-progress-circular>
                      <b-button
                        v-else
                        type="submit"
                        variant="primary"
                        class="btn-block"
                      >
                        {{$t('Submit')}}</b-button
                      >
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <!-- end row -->
          </div>
        </div>
        <!-- end card -->
      </div>
      <div
        class="order-1 order-sm-12"
        :class="
          expandForm
            ? 'col-md-1 col-sm-12  d-flex justify-content-end'
            : 'col-md-3 col-sm-12'
        "
      >
        <Faqs :faqs="faqs" title="Tickets" @toggleForm="toggleFormListener">
          <v-expansion-panels>
            <v-expansion-panel class="panel">
              <v-expansion-panel-header class="panel__header">
                Question 1
                <template v-slot:actions>
                  <v-icon color="pink"> $expand </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="panel__content opacity-75">
                Answer Answer
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel class="panel">
              <v-expansion-panel-header class="panel__header">
                How long would it take for the order to be executed?
                <template v-slot:actions>
                  <v-icon color="pink"> $expand </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="panel__content opacity-75">
                To enable automatic updates for Admin Columns Pro, enter your
                license key. If you don't have a license key, please see details
                & pricing.
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </Faqs>
      </div>
      <!-- end col -->
    </div>
    <v-snackbar
      color="success accent-2"
      elavation="24"
      top
      right
      v-model="isSuccess"
    >
      {{ successMessage }}
    </v-snackbar>
  </Layout>
</template>
